import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ShipmentSplitRefs
 * @property {HTMLElement} [immediateLine]
 * @property {HTMLElement} [backorderLine]
 */

class ShipmentSplitStatus extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    this.#updateDisplay(this.#getSelectedQuantity());
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newStatus = event.detail.data.html.querySelector('shipment-split-status');
    if (!newStatus) return;

    for (const attribute of newStatus.attributes) {
      if (attribute.name.startsWith('data-')) {
        this.setAttribute(attribute.name, attribute.value);
      }
    }

    morph(this, newStatus, { childrenOnly: true });
    this.#updateDisplay(this.#getSelectedQuantity());
  };

  /**
   * @param {CustomEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    const section = this.closest('.shopify-section, dialog');
    if (!section || !event.target || !section.contains(/** @type {Node} */ (event.target))) return;

    this.#updateDisplay(event.detail.quantity);
  };

  /**
   * Re-reads how much of this variant is already in the cart, since stock
   * claimed by earlier adds should count towards pre-order.
   */
  #handleCartUpdate = async () => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) return;

      /** @type {{ items: { variant_id: number, quantity: number }[] }} */
      const cart = await response.json();
      const variantId = Number(this.dataset.variantId);
      const inCartQuantity = cart.items
        .filter((item) => item.variant_id === variantId)
        .reduce((total, item) => total + item.quantity, 0);

      this.dataset.inCartQuantity = inCartQuantity.toString();
      this.#updateDisplay(this.#getSelectedQuantity());
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * @returns {number}
   */
  #getSelectedQuantity() {
    const section = this.closest('.shopify-section, dialog');
    const quantityInput = section?.querySelector('input[name="quantity"]');

    if (quantityInput instanceof HTMLInputElement) {
      const value = parseInt(quantityInput.value, 10);
      if (!Number.isNaN(value)) return value;
    }

    const defaultQuantity = parseInt(this.dataset.defaultQuantity || '1', 10);
    return Number.isNaN(defaultQuantity) ? 1 : defaultQuantity;
  }

  /**
   * @param {number} quantity
   */
  #updateDisplay(quantity) {
    const inventoryManaged = this.dataset.inventoryManaged === 'true';
    const inventoryPolicy = this.dataset.inventoryPolicy;
    const inventoryQuantity = Math.max(0, parseInt(this.dataset.inventoryQuantity || '0', 10) || 0);
    const inCartQuantity = Math.max(0, parseInt(this.dataset.inCartQuantity || '0', 10) || 0);
    const availableQuantity = Math.max(0, inventoryQuantity - inCartQuantity);
    const showImmediateLine = this.dataset.showImmediateLine !== 'false';

    /** @type {ShipmentSplitRefs} */
    const { immediateLine, backorderLine } = this.refs ?? {};

    if (!inventoryManaged || inventoryPolicy !== 'continue') {
      this.hidden = true;
      return;
    }

    const immediateCount = Math.min(quantity, availableQuantity);
    const backorderCount = Math.max(0, quantity - availableQuantity);

    if (backorderCount <= 0) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    if (immediateLine instanceof HTMLElement) {
      if (showImmediateLine && immediateCount > 0) {
        immediateLine.hidden = false;
        immediateLine.textContent = this.#formatMessage('immediate', immediateCount);
      } else {
        immediateLine.hidden = true;
      }
    }

    if (backorderLine instanceof HTMLElement) {
      backorderLine.hidden = false;
      backorderLine.textContent = this.#formatMessage('backorder', backorderCount);
    }
  }

  /**
   * @param {'immediate' | 'backorder'} type
   * @param {number} count
   * @returns {string}
   */
  #formatMessage(type, count) {
    const suffix = count === 1 ? 'One' : 'Other';
    const template = this.dataset[`${type}${suffix}`] || this.dataset[`${type}Other`] || '';

    return template.replace(/__COUNT__/g, count.toString());
  }

  /**
   * @returns {ShipmentSplitRefs}
   */
  get refs() {
    return {
      immediateLine: this.querySelector('[ref="immediateLine"]') ?? undefined,
      backorderLine: this.querySelector('[ref="backorderLine"]') ?? undefined,
    };
  }
}

if (!customElements.get('shipment-split-status')) {
  customElements.define('shipment-split-status', ShipmentSplitStatus);
}
